/**
 * CannotAnswerExit — the "I don't have this information" exit on a blocked
 * `ask_user_for_info` form card. Offers two paths out: reassign the proposal
 * to a colleague, or reject it with `category: 'cannot_answer'` and an
 * optional note.
 */

import { SwitchHorizontal01, UserX01 } from '@untitledui/icons';
import type { Membership } from 'api-client';
import { useCallback, useId, useRef, useState } from 'react';

import { useUser } from '@/allauth_auth/hooks.js';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import { Button } from '@/lib/base/buttons/button';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { OrgMemberPicker } from '@/lib/crm/record/task';
import { useReassignProposal, useRejectProposal } from '@/lib/proposals';
import { getUserFriendlyMessage } from '@/lib/proposals/core/errors';

interface CannotAnswerExitProps {
  orgId: number;
  proposalId: number;
  /**
   * Reject-path begin: renderer clears its pending autosave debounce and
   * freezes autosave. The cannot-answer reject discards the in-progress
   * answers, so pending edits are dropped rather than saved.
   */
  onExitBegin: () => void;
  /**
   * Reassign-path begin: renderer FLUSHES pending answers — while the current
   * user is still the assignee, since the backend rejects update_payload from
   * the previous assignee once reassigned — and then freezes autosave. Awaited
   * so the save completes before the reassign mutation fires.
   */
  onReassignBegin: () => Promise<void>;
  /** Renderer unfreezes autosave after a FAILED exit. */
  onExitAbort: () => void;
  /**
   * Renderer releases its autosave freeze after a SUCCESSFUL reassign. A
   * reassign leaves the proposal PENDING (unlike a reject, which resolves it),
   * so the freeze must be lifted here or autosave stays frozen on the reused,
   * unkeyed coordination provider. The reject path has no equivalent callback:
   * its success intentionally keeps the freeze held.
   */
  onReassignSuccess: () => void;
}

export function CannotAnswerExit({
  orgId,
  proposalId,
  onExitBegin,
  onReassignBegin,
  onExitAbort,
  onReassignSuccess,
}: CannotAnswerExitProps) {
  const currentUser = useUser() as { id?: number } | null;
  const currentUserId = currentUser?.id;
  const { members } = useOrgMembers(orgId);
  const reassign = useReassignProposal(orgId);
  const reject = useRejectProposal(orgId);
  const panelId = useId();
  const errorId = useId();

  const [expanded, setExpanded] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  // A decision runs an async PREFLIGHT (the reassign path awaits
  // onReassignBegin to flush pending answers; the reject path runs
  // onExitBegin) before its mutation fires. During that window neither
  // mutation isPending yet, so `preflight` covers it — set synchronously at
  // the start of a decision and cleared when it settles — keeping BOTH exit
  // paths and Cancel disabled from the first click, not only once the mutation
  // is in flight. Without it a second click could race a duplicate mutation,
  // and Cancel would appear to work while the reassign still fired.
  const [preflight, setPreflight] = useState(false);

  // Synchronous re-entry guard: React state updates are not observed until the
  // next render, so `preflight` alone cannot block a second invocation dispatched
  // in the same tick. This ref rejects re-entry immediately.
  const decidingRef = useRef(false);

  // The two exit paths share this mutation pair, so only one may be in
  // flight at a time.
  const isDeciding = preflight || reassign.isPending || reject.isPending;

  // Tracks whether the panel has ever been opened, so the collapsed trigger
  // only reclaims focus after collapsing from a prior open — never on the
  // component's initial mount.
  const hasOpenedRef = useRef(false);

  const openExit = useCallback(() => {
    hasOpenedRef.current = true;
    setExpanded(true);
  }, []);
  const revealReason = useCallback(() => setShowReason(true), []);
  const handleReasonChange = useCallback(
    (value: string) => setReason(value),
    []
  );

  // Ref-callback focus: fires on mount, so the panel's first control gets
  // focus as soon as it expands, and the reason field gets focus as soon as
  // it appears — no effect needed.
  const focusFirstControlRef = useCallback(
    (node: HTMLButtonElement | HTMLAnchorElement | null) => node?.focus(),
    []
  );
  const focusReasonRef = useCallback(
    (node: HTMLTextAreaElement | null) => node?.focus(),
    []
  );
  const focusTriggerRef = useCallback(
    (node: HTMLButtonElement | HTMLAnchorElement | null) => {
      if (hasOpenedRef.current) {
        node?.focus();
      }
    },
    []
  );

  // Collapse the panel and clear its transient reason/error state. No autosave
  // freeze needs lifting here: freezing happens inside doReassign /
  // handleCannotAnswer right before their mutation fires, so a panel that was
  // only opened (Cancel) never armed one. Reused by Cancel and by the
  // reassign-success path — the renderer can stay mounted after a reassign, so
  // a note typed before reassigning must not survive into a reopened panel.
  const resetPanel = useCallback(() => {
    setExpanded(false);
    setShowReason(false);
    setReason('');
    setError(null);
  }, []);

  const doReassign = useCallback(
    async (userId: number) => {
      // Reject re-entry and mark the decision busy BEFORE the async preflight,
      // so both exit paths and Cancel disable from the first click — the flush
      // (onReassignBegin) runs while no mutation is pending yet.
      if (decidingRef.current) {
        return;
      }
      decidingRef.current = true;
      setPreflight(true);
      setError(null);
      try {
        // Flush pending answers (while still the assignee), then freeze.
        await onReassignBegin();
        await reassign.mutateAsync({
          proposalId,
          request: { new_assignee_user_id: userId },
        });
        // Reassign succeeded: the proposal stays PENDING with a new assignee, so
        // the renderer must lift the freeze it armed in onReassignBegin (a
        // reject, which resolves the proposal, keeps its hold instead). Fully
        // reset the panel — not just collapse it — so a note typed before
        // reassigning cannot be resubmitted if the still-mounted exit reopens.
        onReassignSuccess();
        resetPanel();
      } catch (e) {
        onExitAbort();
        setError(getUserFriendlyMessage(e));
      } finally {
        decidingRef.current = false;
        setPreflight(false);
      }
    },
    [
      reassign,
      proposalId,
      onReassignBegin,
      onExitAbort,
      onReassignSuccess,
      resetPanel,
    ]
  );

  const handleSelectMember = useCallback(
    (entityId: string) => {
      const member = members.find((m: Membership) => m.entityId === entityId);
      const userId = member?.userId;
      if (!userId) {
        setError("We couldn't find that colleague. Try again.");
        return;
      }
      if (userId === currentUserId) {
        setError(
          'This proposal is already assigned to you. Choose someone else.'
        );
        return;
      }
      void doReassign(userId);
    },
    [members, currentUserId, doReassign]
  );

  const handleCannotAnswer = useCallback(async () => {
    // Reject re-entry and mark the decision busy synchronously, before the
    // begin/mutation sequence, so a second click cannot race a duplicate
    // cannot-answer mutation.
    if (decidingRef.current) {
      return;
    }
    decidingRef.current = true;
    setPreflight(true);
    setError(null);
    try {
      onExitBegin();
      await reject.mutateAsync({
        proposalId,
        request: { category: 'cannot_answer', reason },
      });
      setExpanded(false);
    } catch (e) {
      onExitAbort();
      setError(getUserFriendlyMessage(e));
    } finally {
      decidingRef.current = false;
      setPreflight(false);
    }
  }, [reject, proposalId, reason, onExitBegin, onExitAbort]);

  if (!expanded) {
    return (
      <Button
        color='secondary'
        size='sm'
        iconLeading={UserX01}
        onClick={openExit}
        aria-expanded={false}
        aria-controls={panelId}
        ref={focusTriggerRef}
      >
        I don&apos;t have this information
      </Button>
    );
  }

  return (
    <div
      id={panelId}
      role='group'
      aria-label="I don't have this information"
      className='space-y-3 rounded-lg border border-border-secondary bg-bg-secondary p-3'
    >
      <div className='flex items-start gap-2.5'>
        <UserX01 className='mt-0.5 size-4 shrink-0 text-fg-quaternary' />
        <div className='space-y-0.5'>
          <p className='text-sm font-medium text-fg-primary'>
            Who has this information?
          </p>
          <p className='text-xs text-fg-tertiary'>
            Hand this form to a colleague who was there, or let us know nobody
            can answer it.
          </p>
        </div>
      </div>
      <div className='flex flex-wrap items-center gap-2'>
        <OrgMemberPicker
          orgId={orgId}
          selectedEntityId={null}
          onSelect={handleSelectMember}
          // Gate the popover open, not just the trigger's onClick. The trigger
          // below carries a `disabledTooltip`, so while `isDeciding` it renders
          // through the gated aria-disabled path (button.tsx) which keeps
          // react-aria's usePress active — DialogTrigger would otherwise still
          // open the picker on press mid-decision. Disabling the picker itself
          // is what keeps the exit locked. Do NOT drop the trigger's
          // disabledTooltip to force native-disabled instead: a disabled
          // <Button> with no tooltip trips the disabled-button-tooltip lint.
          isDisabled={isDeciding}
          aria-label='Reassign to a colleague'
        >
          <Button
            // Primary until the user picks the "nobody" path; from then on
            // Confirm is the one primary action in the panel.
            color={showReason ? 'secondary' : 'primary'}
            size='xs'
            iconLeading={SwitchHorizontal01}
            isDisabled={isDeciding}
            disabledTooltip={isDeciding ? 'Submitting…' : undefined}
            ref={focusFirstControlRef}
          >
            Choose a colleague
          </Button>
        </OrgMemberPicker>
        <Button
          color='secondary'
          size='xs'
          onClick={revealReason}
          isDisabled={isDeciding}
          disabledTooltip={isDeciding ? 'Submitting…' : undefined}
        >
          Nobody / not sure
        </Button>
        <Button
          color='link-gray'
          size='xs'
          onClick={resetPanel}
          isDisabled={isDeciding}
          disabledTooltip={isDeciding ? 'Submitting…' : undefined}
          className='ml-auto'
        >
          Cancel
        </Button>
      </div>
      {showReason && (
        <div className='space-y-2 border-t border-border-secondary pt-3'>
          <TextArea
            value={reason}
            onChange={handleReasonChange}
            placeholder='Add a note (optional)'
            aria-label='Reason you cannot answer'
            aria-describedby={error ? errorId : undefined}
            ref={focusReasonRef}
          />
          <div className='flex justify-end'>
            <Button
              color='primary'
              size='xs'
              onClick={handleCannotAnswer}
              isDisabled={isDeciding}
              disabledTooltip={isDeciding ? 'Submitting…' : undefined}
            >
              Confirm
            </Button>
          </div>
        </div>
      )}
      {error && (
        <p
          id={errorId}
          role='alert'
          className='text-sm text-[var(--color-text-error-primary)]'
        >
          {error}
        </p>
      )}
    </div>
  );
}
